import React from "react";
import { FaPlay, FaRocket, FaUsers, FaStar } from "react-icons/fa6";
import { Container, GradientButton } from "./ui";

// Hero artwork (laptop + tech icons), shown in the right column.
const heroImg = "/img/hero-bg1.jpg";

// Fades all four edges of the image to transparent (two linear masks intersected),
// so the photo's own background never shows as a box against the section.
const fadeX = "linear-gradient(to right, transparent 0%, #000 14%, #000 88%, transparent 100%)";
const fadeY = "linear-gradient(to bottom, transparent 0%, #000 10%, #000 84%, transparent 100%)";
const edgeFade = {
  WebkitMaskImage: `${fadeX}, ${fadeY}`,
  WebkitMaskComposite: "source-in",
  maskImage: `${fadeX}, ${fadeY}`,
  maskComposite: "intersect",
};

const stats = [
  { icon: FaRocket, value: "100+", label: "Projects Delivered", tone: "bg-violet-100 text-violet-600" },
  { icon: FaUsers, value: "50+", label: "Happy Clients", tone: "bg-blue-100 text-blue-600" },
  { icon: FaStar, value: "4.9/5", label: "Client Satisfaction", tone: "bg-orange-100 text-orange-500" },
];

const Hero = () => (
  <section
    id="technologies"
    className="relative overflow-hidden bg-gradient-to-r from-[#FAFAFC] via-[#F7F8FE] to-[#F1F4FE] pb-[48px] pt-[110px] xl:flex xl:min-h-[min(40.6vw,780px)] xl:items-center xl:pb-[40px] xl:pt-[calc(clamp(76px,5.83vw,112px)+16px)]"
  >

    <Container className="relative grid max-w-[1900px] items-center gap-[32px] xl:grid-cols-[44%_1fr] xl:gap-[clamp(16px,2vw,48px)] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
      <div className="max-w-[640px] xl:max-w-none">
        <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-[12px] py-1 text-[12px] font-semibold uppercase tracking-wide text-ink/80 ring-1 ring-blue-100">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          We build digital experiences
        </span>

        <h1 className="mt-4 text-[60px] font-extrabold leading-[1] tracking-tight text-ink ">
          Modern Web Development 
        </h1>
        <h1 className=" text-[56px] font-extrabold leading-[1] tracking-tight"> 
          <span className="text-gradient">for Real Growth</span>
        </h1>

        <p className="mt-4 max-w-xl text-[14px] leading-relaxed text-slate-500 sm:text-[14px] xl:max-w-[700px] ">
          We create fast, scalable, and user-friendly web solutions that help businesses grow in the digital world.
          From stunning websites to powerful web applications, we turn ideas into reality.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-[14px]">
          <GradientButton href="/contact-us" className="px-8 text-[12px]">
            Get Started
          </GradientButton>
          <a
            href="/our-completed-projects"
            className="inline-flex items-center gap-[12px] rounded-full bg-white py-2 pl-2 pr-7 text-[16px] font-semibold text-ink ring-1 ring-slate-200 transition hover:ring-brand-blue"
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-brand text-[12px] text-white">
              <FaPlay className="ml-0.5" />
            </span>
            Watch Our Work
          </a>
        </div>

        <div className="mt-[40px] grid grid-cols-1 gap-[16px] sm:grid-cols-3 sm:gap-0">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`flex items-center gap-[10px] sm:pr-[14px] ${i > 0 ? "sm:border-l sm:border-slate-200 sm:pl-[16px]" : ""}`}
            >
              <span className={`inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full text-[18px] ${s.tone}`}>
                <s.icon />
              </span>
              <div>
                <p className="text-[22px] font-extrabold leading-none text-ink">{s.value}</p>
                <p className="mt-1 whitespace-nowrap text-[13px] text-slate-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Artwork: right column on desktop, below the text on mobile/tablet */}
      <div className="flex justify-center xl:justify-end">
        <img
          src={heroImg}
          alt="Developer workspace with a laptop and modern web technologies"
          className="h-auto max-h-[480px] w-full max-w-[min(100%,860px)] select-none object-contain xl:w-auto"
          style={edgeFade}
        />
      </div>
    </Container>
  </section>
);

export default Hero;
